import React, { Suspense, useEffect, useMemo, useState, useTransition } from "react";
import { Col, Row } from "reactstrap";  
import DetailViewLayout from "components/list/DetailViewLayout";
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useList } from "hooks/useList";
import { routeObjects } from "mappings/RouteObjects";
import LoaderComponent from "components/common/Loader";
import CompaniesDashboard from "components/modules/companies/CompanyDashboard";
import ErrorBoundary from "components/common/ErrorBoundary"
import { getListComponent } from "utils/moduleRegistry";

const ADMIN_PATH = '/admin';
const DASHBOARD_PATH = '/dashboard';

const pageTypeMap = Object.fromEntries(
    routeObjects.map(obj => [`${ADMIN_PATH}/${obj.obj}`, obj.obj])
);

function ListDetailManager() {
    
    const location = useLocation();
    const navigate = useNavigate();
    const [isPending, startTransition] = useTransition();
    const endsWithDashboard = location.pathname.endsWith(DASHBOARD_PATH); 
    const [showReports, setShowReports] = useState(endsWithDashboard);

    const getPageType = () => {
        const matchedPath = Object.keys(pageTypeMap).find(path => location.pathname.startsWith(path));
        return matchedPath ? pageTypeMap[matchedPath] : null;
    };

    const type = useMemo(getPageType, [location.pathname]);
    const ListComponent = useMemo(() => getListComponent(type), [type]);

    const params = useParams();
    const { id: idFromParams, uploadId: uploadIdFromParams } = useParams();
    const getItemFromUrl = () => { return Number.isInteger(Number(idFromParams)) ? { type, id: Number(idFromParams) } : null;}

    const [selectedItem, setSelectedItem] = useState(getItemFromUrl());
    const [lastSelection, setLastSelection] = useState (null); 

    const list = useList(type);

    const toggleReports = () => {
        startTransition(() => {
            
            if(!showReports) {
                navigate(`/admin/${type}${DASHBOARD_PATH}`, { replace: false });
            } else {
                navigate(`/admin/${type}/`, { replace: false })
            }
            setShowReports(prev => !prev)
        });
    }

    const handleSelect = (item) => {
        startTransition(() => {
            setSelectedItem(item)
            if (item === null) {
                navigate(`/admin/${type}/`, { replace: false });
            }
            else if (type === item.type) {
                if(item.uploadId)
                    navigate(`/admin/${type}/${item.id}/uploads/${item.uploadId}`, { replace: false })
                else
                    navigate(`/admin/${type}/${item.id}`, { replace: false })
            }
            else if (item?.parentId) {
                navigate(`/admin/${type}/${item.parentId}/${item.type}/${item.id}`, { replace: false })
            }
        });
    }

    /*useEffect(() => {
        if(listUrl){
            if(endsWithDashboard && !showReports || !endsWithDashboard && showReports) {
                toggleReports()
            }
        }
    }, [listUrl]);*/

    useEffect(() => {
        if (((selectedItem?.parentId && !idFromParams) || (!selectedItem?.parentId && (idFromParams !== selectedItem?.id || type != selectedItem?.type)))) {
            const parsedId = parseInt(idFromParams, 10)
            const uploadId = parseInt(uploadIdFromParams, 10)
            if(isNaN(parsedId)){
                if(endsWithDashboard){
                    setSelectedItem(null);
                    navigate(`/admin/${type}${DASHBOARD_PATH}`, { replace: false });
                } else {
                    setSelectedItem(null);
                    navigate(`/admin/${type}/`, { replace: false });
                }
            } else {
                setSelectedItem({type, id: parsedId, uploadId: isNaN(uploadId) ? selectedItem?.uploadId : uploadId});
            }
        }
    }, [idFromParams]);

    if(!ListComponent) return <div>List Components not found</div>

    return (
    <div className="content">
        <Row>
            <Col md="12">
                {showReports ?
                    <CompaniesDashboard 
                        selectedItem={selectedItem} 
                        toggleReports={toggleReports}
                    />
                : selectedItem != null ? 
                    <ErrorBoundary fallback={<div>An error occurred in the DetailViewLayout.</div>}>
                        <Suspense fallback={<LoaderComponent/>}>
                            <DetailViewLayout
                                key={selectedItem.id}
                                list={list}
                                selectedItem={selectedItem} 
                                setSelectedItem={handleSelect} 
                            />
                        </Suspense>
                    </ErrorBoundary>
                : 
                    <Suspense fallback={<LoaderComponent/>}>
                        <ListComponent
                            type={type}
                            data={list}
                            selectedItem={selectedItem} 
                            setSelectedItem={handleSelect} 
                            toggleReports={toggleReports}
                            lastSelection={lastSelection}
                            setLastSelection={setLastSelection}
                        />
                    </Suspense>
                }
            </Col>
        </Row>
    </div>)
}

export default ListDetailManager;